import Sidebar from 'components/layout/Sidebar';
import styled from 'styled-components';
import CreditCardDailyPrintHeader from './CreditCardDailyPrintHeader';
import { useLocation, useNavigate } from 'react-router-dom';
import PayrollReceiptWrapper from 'features/report/pages/ReportPage/EmployeeSalary/Previews/PayrollReceiptWrapper';
import Text from 'components/Text';
import Box from 'components/Box';
import { formatCurrency } from 'utils/formatCurrency';
import ticketListApis from 'features/ticketList/services/apis';
import { useEffect, useRef, useState } from 'react';
import { IResponseDataBody } from 'services/response';
import { IDejavooDailyReportResData } from 'features/ticketList/services/types/creditCard';
import { useReactToPrint } from 'react-to-print';

const CreditCardDailyReportPage = () => {
  const [dejavooReportData, setDejavooReportData] = useState<IDejavooDailyReportResData | null>(null);
  const location = useLocation();
  const stationNumber = location.state?.stationNumber;
  const startDate = location.state?.startDate;

  const navigate = useNavigate();

  const printRef = useRef<HTMLDivElement>(null);
  const onPrint = useReactToPrint({
    content: () => printRef.current,
    copyStyles: true,
  });

  const onBack = () => {
    navigate(-1);
  };

  useEffect(() => {
    const getDejavooDailyReport = async () => {
      try {
        const response: IResponseDataBody<IDejavooDailyReportResData> = await ticketListApis.getDejavooDailyReport(
          startDate,
          stationNumber,
        );

        if (response?.data.data) {
          setDejavooReportData(response.data.data);
        }
      } catch (error) {
        console.error('Error fetching Dejavoo daily report:', error);
        throw error;
      }
    };
    getDejavooDailyReport();
  }, []);

  const handlePrintOverview = () => {
    if (!printRef.current) {
      // @ts-ignore
      printRef.current.style.display = 'block';
    }
    setTimeout(() => onPrint(), 1);
  };

  return (
    <Wrapper>
      <Sidebar />
      <Container>
        <CreditCardDailyPrintHeader onBack={onBack} onPrint={handlePrintOverview} />
        <div ref={printRef}>
          <PrintContainer>
            <PayrollReceiptWrapper>
              <Content>
                <Text variant='H5' className='header-print' printMode>
                  Daily Batch Reports
                </Text>
                <Daily>
                  <Text variant='H9' printMode>
                    {dejavooReportData?.createdDate}
                  </Text>
                </Daily>
                <Box width='100%' display='flex' flexDirection='column' style={{ gap: '2px', borderBottom: '1px dashed #000', paddingBottom: '4px' }}>
                  <InfoRow>
                    <Text variant='CAPTION_2' printMode>Terminal Number</Text>
                    <Text variant='CAPTION_2' printMode>{dejavooReportData?.tpn}</Text>
                  </InfoRow>

                  <InfoRow>
                    <Text variant='CAPTION_2' printMode>Batch Number</Text>
                    <Text variant='CAPTION_2' printMode>{dejavooReportData?.batchNumber}</Text>
                  </InfoRow>

                  <InfoRow>
                    <Text variant='CAPTION_2' printMode>Terminal ID</Text>
                    <Text variant='CAPTION_2' printMode>{dejavooReportData?.terminalId}</Text>
                  </InfoRow>
                </Box>
                <TableStyled>
                  <thead>
                    <tr>
                      <th style={{ textAlign: 'start' }}>TRANS#</th>
                      <th style={{ textAlign: 'end' }}>LAST 4</th>
                      <th style={{ textAlign: 'end' }}>CRD</th>
                      <th style={{ textAlign: 'end' }}>AMOUNT</th>
                    </tr>
                  </thead>

                  <tbody>
                    {dejavooReportData?.transactions?.map((item, i) => (
                      <tr key={i}>
                        <td className='ticket-col'>#{item?.Number}</td>
                        <td className='end'>{item?.Last4}</td>
                        <td className='end'>{item?.CardType}</td>
                        <td className='end'>{formatCurrency(item?.Amount)}</td>
                      </tr>
                    ))}
                  </tbody>
                </TableStyled>
                <Box width='100%' display='flex' flexDirection='column' style={{ borderTop: '1px dashed #000', paddingTop: '4px'}}>
                  <InfoRow>
                    <Text variant='CAPTION_4' printMode style={{ fontWeight: 600 }}>Records:</Text>
                    <Text variant='CAPTION_2' printMode>{dejavooReportData?.totalTransactions}</Text>
                  </InfoRow>

                  <InfoRow>
                    <Text variant='CAPTION_4' printMode style={{ fontWeight: 600 }}>Total:</Text>
                    <Text variant='CAPTION_2' printMode>{formatCurrency(dejavooReportData?.total)}</Text>
                  </InfoRow>

                  <InfoRow>
                    <Text variant='CAPTION_4' printMode style={{ fontWeight: 600 }}>Total tip:</Text>
                    <Text variant='CAPTION_2' printMode>{formatCurrency(dejavooReportData?.totalTip)}</Text>
                  </InfoRow>

                  <InfoRow>
                    <Text variant='CAPTION_4' printMode style={{ fontWeight: 600 }}>Total w/o tip:</Text>
                    <Text variant='CAPTION_2' printMode>{formatCurrency(dejavooReportData?.totalWithoutTip)}</Text>
                  </InfoRow>

                  <InfoRow>
                    <Text variant='CAPTION_4' printMode style={{ fontWeight: 600 }}>Non Cash Adj Total:</Text>
                    <Text variant='CAPTION_2' printMode>{formatCurrency(dejavooReportData?.nonCashAdjustment)}</Text>
                  </InfoRow>

                  <InfoRow>
                    <Text variant='CAPTION_4' printMode style={{ fontWeight: 600 }}>TOTAL w/o Non Cash Adj:</Text>
                    <Text variant='CAPTION_2' printMode>{formatCurrency(dejavooReportData?.totalWithoutAdjustment)}</Text>
                  </InfoRow>

                  <InfoRow>
                    <Text variant='CAPTION_4' printMode style={{ fontWeight: 600 }}>TOTAL w/o fee/tip:</Text>
                    <Text variant='CAPTION_2' printMode>{formatCurrency(dejavooReportData?.totalWithoutFeeAndTip)}</Text>
                  </InfoRow>
                </Box>

                <Center>
                  <Text variant='CAPTION_4' style={{ fontWeight: 500 }} printMode>
                    End of report - Powered by Fozito
                  </Text>
                </Center>
                <TicketCodeBox>POS Stations #{stationNumber}</TicketCodeBox>
              </Content>
            </PayrollReceiptWrapper>
          </PrintContainer>
        </div>
      </Container>
    </Wrapper>
  );
};

export default CreditCardDailyReportPage;

const Wrapper = styled.div`
  display: flex;
`;

const Container = styled.div`
  background-color: #f1f5f9;
  flex-grow: 1;
  height: 100vh;
  overflow: auto;
  position: relative;
  width: 100%;

  .print-text {
    padding: 0;
  }
`;

const PrintContainer = styled.div`
  .styled-print {
    padding-top: 24px;
    padding-bottom: 24px;
    border-radius: 4px;
  }
`;

const Content = styled.div`
  margin-top: 8px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  width: 100%;

  .header-print {
    text-align: center;
    font-family: 'Montserrat';
    font-size: 20px;
    font-weight: 800;
    line-height: 1;
  }
`;

const Daily = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  border-top: 1px dashed #000;
  border-bottom: 1px dashed #000;
  margin: 4px 0px;
`;

const InfoRow = styled.div`
  display: flex;
  justify-content: space-between;
  width: 100%;

  .CAPTION_2 {
    font-size: 10px;
    font-weight: 700;
    line-height: 1;
  }

  .CAPTION_4 {
    line-height: 1;
  }
`;

const Center = styled.div`
  width: 100%;
  text-align: center;
  border-bottom: 1px dashed #000;
  padding: 4px 0px;
`;

const TableStyled = styled.table`
  width: 100%;
  border-collapse: collapse;
  padding-bottom: 4px;

  thead {
    th {
      font-family: 'Roboto', serif !important;
      color: black !important;
      font-size: 12px;
      font-weight: 600;
      border-bottom: 1px dashed #000;
    }
  }

  tbody {
    margin-top: 4px;
    td {
      font-family: 'Roboto', serif !important;
      color: black !important;
      font-size: 10px;
      font-weight: 600;
      line-height: 1;
    }

    :first-child {
      td {
        padding-top: 2px;
      }
    }
  }

  .ticket-col {
    width: 55px;
  }

  .start {
    text-align: start;
  }

  .center {
    text-align: center;
  }

  .right {
    text-align: right;
  }

  .end {
    text-align: end;
  }
`;

const TicketCodeBox = styled.div`
  background: black;
  display: flex;
  padding: 4px 8px;
  justify-content: center;
  align-items: center;
  gap: 10px;
  color: #fff;

  font-size: 20px;
  font-style: normal;
  font-weight: 500;
  line-height: normal;
  font-family: 'Poppins';
  margin-top: 4px;
`;
